import { useEffect, useState } from 'react';

/**
 * Bare hash routing — Family
 *
 * Replaces React Router (CLAUDE.md §3 v2.2 simplification). Bundle savings
 * ~10KB gzipped. Hash routing is also cPanel-friendly (no server-side rewrite
 * rules needed for SPA fallback).
 *
 * Routes:
 *   '/'                      → Home
 *   '/onboarding'            → Onboarding (3 screens, internally stepped)
 *   '/chat/<id>'             → Chat with conversation id
 *   '/face-to-face/<userId>' → Face-to-face conversation with contact
 *   '/settings'              → Settings
 *
 * Note: hash routing means URLs look like https://family.../#/settings
 *       This is fine for our family use case and avoids server config.
 *
 * Query strings (A6 — iPad two-pane): the hash can carry a `?key=value`
 * suffix used by the tablet two-pane layout to select a contact without
 * changing the visible route, e.g. `#/?c=<convId>`. Path and query are
 * parsed here so call sites don't reach for window.location directly.
 */

export function useHashRoute(): string {
  const [hash, setHash] = useState<string>(() =>
    typeof window !== 'undefined' ? readHash() : '/',
  );

  useEffect(() => {
    function handler() {
      setHash(readHash());
    }
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // Path component only (query stripped) for back-compat with existing callers.
  const qIdx = hash.indexOf('?');
  return qIdx === -1 ? hash : hash.slice(0, qIdx);
}

/**
 * Hash route split into `{ path, query }`. Returns a fresh `URLSearchParams`
 * each render — cheap, and prevents accidental external mutation.
 *
 * Used by the iPad two-pane layout to drive the right-pane chat without
 * leaving the contact-list route. See App.tsx.
 */
export function useHashRouteWithQuery(): { path: string; query: URLSearchParams } {
  const [hash, setHash] = useState<string>(() =>
    typeof window !== 'undefined' ? readHash() : '/',
  );

  useEffect(() => {
    function handler() {
      setHash(readHash());
    }
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  return parseHash(hash);
}

function readHash(): string {
  return window.location.hash.slice(1) || '/';
}

function parseHash(hash: string): { path: string; query: URLSearchParams } {
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return { path: hash, query: new URLSearchParams() };
  return {
    path: hash.slice(0, qIdx),
    query: new URLSearchParams(hash.slice(qIdx + 1)),
  };
}

export function navigate(path: string): void {
  if (typeof window !== 'undefined') {
    window.location.hash = path;
  }
}
